import { useState, useEffect, useCallback } from "react";
import "./ManufactureKiwi.scss";
import { notification, DatePicker } from "antd";
import { macValidation, ssidValidation } from "../../../utils/formValidation";
import service from "../../../services/Service";
import dayjs from 'dayjs';
import { withLocale } from "../../../l10n";
import Loading from "../../../utils/loading";
import withNavigation from '../../../components/hooks/withNavigation';
import { useGetVersionsLookupQuery } from '../../../services/lookupApi.js';

function ManufactureKiwi({ lang, navigate, newBoard, ident, editable }) {
  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
  });

  const [form, setForm] = useState({
    ssid: "",
    mac: "",
    date: dayjs().format('DD-MM-YYYY'),
    notes: "",
    state: "Fabricado",
    version_uuid: "",
  });

  const [errors, setErrors] = useState({
    ssid: "",
    mac: "",
  });

  // ===== 2. SELECTORES =====
  const { data: versions = [] } = useGetVersionsLookupQuery();

  // ===== 3. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateErrors = (field, value) => setErrors(prev => ({ ...prev, [field]: value }));

  // ===== 4. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    if (!ident) return;

    updateUI("loading", true);
    try {
      const res = await service.get("kiwi/" + ident);
      if (res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res.data.message),
        });
        return;
      }
      const data = res.data.data;
      setForm({
        order_id: data.order_id,
        serial_number: data.serial_number,
        work_order: data.work_order,
        ssid: data.ssid,
        mac: data.mac,
        uuid: data.uuid,
        date: dayjs(data.date).format("DD-MM-YYYY"),
        version_uuid: data.version_uuid,
        state: data.state,
        notes: data.notes,
        _id: data._id,
      });
    } catch (err) {
      console.log("Error devuelto: " + err);
    } finally {
      updateUI("loading", false);
    }
  }, [ident, lang]);

  // ===== 5. HANDLERS =====
  const onInputChange = (e) => {
    const { name, value } = e.target;
    let errorMsg;

    switch (name) {
      case "ssid":
        errorMsg = ssidValidation(value) ? "" : lang.handle("¡SSID erróneo!");
        updateErrors("ssid", errorMsg);
        break;
      case "mac":
        errorMsg = macValidation(value) ? "" : lang.handle("¡MAC errónea!");
        updateErrors("mac", errorMsg);
        break;
      default:
        break;
    }
    updateForm(name, value);
  };

  const onChangeDate = (date, dateString) => {
    updateForm("date", dateString);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const urlStockKibox = "/boards/manufacture/list/2";

    if (!errors.ssid && !errors.mac) {
      try {
        const formData = {
          ssid: form.ssid,
          mac: form.mac,
          date: form.date,
          notes: form.notes,
          state: form.state,
          version_uuid: form.version_uuid,
        };

        if (newBoard) {
          const res = await service.post("kiwi", formData);
          if (!res) return;

          if (res.data.ok === false) {
            notification["error"]({
              message: lang.handle(res.data.message),
            });
          } else {
            notification["success"]({
              message: lang.handle("Placa creada correctamente"),
            });
            navigate(urlStockKibox);
          }
        } else {
          const res = await service.put("kiwi/" + form.uuid, formData);
          if (!res) return;

          if (res.data.ok === false) {
            notification["error"]({
              message: lang.handle(res.data.message),
            });
          } else {
            notification["success"]({
              message: lang.handle("Placa actualizada correctamente"),
            });
            navigate(urlStockKibox);
          }
        }
      } catch (err) {
        console.log("Error: " + err);
      }
    } else {
      if (errors.mac) {
        notification["error"]({
          message: lang.handle("MAC errónea"),
        });
      }
      if (errors.ssid) {
        notification["error"]({
          message: lang.handle("SSID erróneo"),
        });
      }
    }
  };

  // ===== 6. EFFECTS =====
  useEffect(() => {
    if (ident) {
      Pfetch();
    }
  }, [ident, Pfetch]);

  // ===== 7. RENDER HELPERS =====
  const kiwiVersions = versions.filter((v) => v.device_type === "kiwi");

  const title = newBoard
    ? lang.handle("Nuevo kiwi")
    : lang.handle("Información de kiwi") + " " + form.ssid;

  // ===== 9. RENDER =====
  return (
    <div className="card">
      {ui.loading && <Loading loading={ui.loading} />}
      <h4 className="p-3"> {title} </h4>
      <form onSubmit={onSubmit} className="p-3">
        <div className="form-group row">
          <label
            htmlFor="ssid"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            SSID
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="ssid"
              placeholder="KIWI_A5BD67"
              onChange={onInputChange}
              name="ssid"
              value={form.ssid}
              disabled={!editable}
              required
            />
            {errors.ssid.length > 0 && (
              <span className="boardFormError">{errors.ssid}</span>
            )}
          </div>
        </div>

        <div className="form-group row">
          <label
            htmlFor="mac"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            MAC
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="mac"
              placeholder="30:A4:F5:20:4C:5A"
              onChange={onInputChange}
              name="mac"
              value={form.mac}
              disabled={!editable}
              required
            />
            {errors.mac.length > 0 && (
              <span className="boardFormError">{errors.mac}</span>
            )}
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text">
            {" "}
            {lang.handle("Fecha fabricación")}{" "}
          </div>
          <div className="col-sm-10">
            <DatePicker
              className="form-control"
              value={dayjs(form.date, "DD-MM-YYYY")}
              onChange={onChangeDate}
              format="DD-MM-YYYY"
              disabled={!editable}
              allowClear={false}
              required
            />
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text">
            {" "}
            {lang.handle("Versión")}{" "}
          </div>
          <div className="col-sm-10">
            <select
              className="form-control"
              onChange={onInputChange}
              name="version_uuid"
              value={form.version_uuid}
              disabled={!editable}
              required
            >
              <option value="">
                {" "}
                -- {lang.handle("Seleccionar opción")} --{" "}
              </option>
              {kiwiVersions
                .map((version) => (
                  <option key={version.uuid} value={version.uuid}>
                    {version.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="form-group row">
          <label
            htmlFor="notes"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle("Notas")}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="notes"
              placeholder={lang.handle("No se conecta al WiFi")}
              onChange={onInputChange}
              name="notes"
              value={form.notes}
              disabled={!editable}
            />
          </div>
        </div>

        {editable && (
          <button
            id="button-save"
            className="btn btn-primary d-flex float-right"
            type="submit"
          >
            <div className="btnText">
              <p> {lang.handle("Guardar")}&nbsp;&nbsp; </p>
              <i className="material-icons"> save </i>
            </div>
          </button>
        )}
      </form>
    </div>
  );
}

export default withLocale(withNavigation(ManufactureKiwi));
